import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DlpProjectModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { DlpIssue } from 'utils/dlpConfigurationValidation';
import { DLP_CHANNELS, DLP_GROUP_BY_OPTIONS, DLP_RANKING_METRICS } from '../dlpConstants';
import { DlpField } from '../Common/DlpField';
import { ChipMultiSelect } from '../Common/ChipMultiSelect';
import { Toggle } from '../Common/Toggle';
import { IssueList } from '../Common/IssueList';
import { SaveBar } from '../Common/SaveBar';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { useReportDirty } from '../Common/useUnsavedChangesGuard';
import { LabelListEditor } from './LabelListEditor';
import { validateDlpLabels } from 'utils/dlpLabelHierarchy';

interface ProjectSettingsPanelProps {
    project: DlpProjectModel;
    linkedScopeCount: number;
    issues: DlpIssue[];
    onSave: (project: DlpProjectModel) => Promise<void>;
    onDirtyChange: (dirty: boolean) => void;
}

// Mount with key={project.id}: the draft is initialised once per project.
export function ProjectSettingsPanel({ project, linkedScopeCount, issues, onSave, onDirtyChange }: ProjectSettingsPanelProps) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<DlpProjectModel>(project);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const patch = (changes: Partial<DlpProjectModel>) => setDraft(d => ({ ...d, ...changes }));
    const stratified = draft.group_by.includes('label');
    const isDirty = JSON.stringify(draft) !== JSON.stringify(project);
    const labelsValid = validateDlpLabels(draft.dlp_labels).length === 0 && (!draft.is_active || draft.dlp_labels.length > 0);
    useReportDirty(isDirty, onDirtyChange);

    const save = async () => {
        setConfirmOpen(false);
        if (!labelsValid) return;
        await onSave(draft);
    };

    return (
        <div className="detail-col wide">
            <div className="detail-header">
                <div>
                    <div className="detail-title">{project.id}</div>
                    <div className="detail-sub">{t('dlpConfiguration.project.linkedScopes', { count: linkedScopeCount })}</div>
                </div>
            </div>

            <IssueList issues={issues} />

            <div className="form-grid">
                <DlpField label={t('dlpConfiguration.project.projectId')} hint={t('dlpConfiguration.project.projectIdHint')}>
                    <div className="static-value">{project.id}</div>
                </DlpField>
                <DlpField label={t('dlpConfiguration.project.rankingMetric')}>
                    <select className="lm-input" value={draft.ranking_metric} onChange={e => patch({ ranking_metric: e.target.value })}>
                        {DLP_RANKING_METRICS.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </DlpField>
                <DlpField label={t('dlpConfiguration.project.windowDays')} hint={t('dlpConfiguration.project.windowDaysHint')}>
                    <input
                        className="lm-input"
                        type="number"
                        min={0}
                        value={draft.window_days ?? ''}
                        placeholder={t('dlpConfiguration.project.windowDaysPlaceholder')}
                        onChange={e => patch({ window_days: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                    />
                </DlpField>
                <DlpField label={t('dlpConfiguration.project.active')}>
                    <Toggle
                        checked={draft.is_active}
                        onChange={v => patch({ is_active: v })}
                        label={t(draft.is_active ? 'dlpConfiguration.project.activeOn' : 'dlpConfiguration.project.activeOff')}
                    />
                </DlpField>
            </div>

            <DlpField label={t('dlpConfiguration.project.channels')} hint={t('dlpConfiguration.project.channelsHint')}>
                <ChipMultiSelect options={DLP_CHANNELS} value={draft.channels} onChange={v => patch({ channels: v })} />
            </DlpField>

            <DlpField label={t('dlpConfiguration.project.groupBy')} hint={t('dlpConfiguration.project.groupByHint')}>
                <ChipMultiSelect options={DLP_GROUP_BY_OPTIONS} value={draft.group_by} onChange={v => patch({ group_by: v })} />
            </DlpField>

            {stratified && (
                <DlpField label={t('dlpConfiguration.project.labelField')} hint={t('dlpConfiguration.project.labelFieldHint')}>
                    <input className="lm-input" value={draft.label_field} placeholder="e.g. gender.values" onChange={e => patch({ label_field: e.target.value })} />
                </DlpField>
            )}

            <div className="form-grid">
                <DlpField label={t('dlpConfiguration.project.excludeOwnedMedia')}>
                    <Toggle
                        checked={draft.exclude_owned_media}
                        onChange={v => patch({ exclude_owned_media: v })}
                        label={t(draft.exclude_owned_media ? 'dlpConfiguration.project.excluded' : 'dlpConfiguration.project.included')}
                    />
                </DlpField>
                <DlpField label={t('dlpConfiguration.project.excludeAlreadySent')} hint={t('dlpConfiguration.project.excludeAlreadySentHint')}>
                    <Toggle
                        checked={draft.exclude_already_sent}
                        onChange={v => patch({ exclude_already_sent: v })}
                        label={t(draft.exclude_already_sent ? 'dlpConfiguration.project.skip' : 'dlpConfiguration.project.resend')}
                    />
                </DlpField>
            </div>

            {stratified && (
                <DlpField label={t('dlpConfiguration.project.emptyLabelFallback')} hint={t('dlpConfiguration.project.emptyLabelFallbackHint')}>
                    <input className="lm-input" value={draft.empty_label_fallback} placeholder="e.g. brand_not_found" onChange={e => patch({ empty_label_fallback: e.target.value })} />
                </DlpField>
            )}

            <DlpField label={t('dlpConfiguration.project.labels')} hint={t('dlpConfiguration.project.labelsHint')}>
                <LabelListEditor projectId={project.id} labels={project.dlp_labels} readOnly onChange={() => {}} />
            </DlpField>

            {isDirty && <SaveBar onClick={() => setConfirmOpen(true)} disabled={!labelsValid} />}
            {confirmOpen && (
                <ConfirmDialog
                    title={t('dlpConfiguration.project.saveTitle')}
                    message={t('dlpConfiguration.project.saveMessage', { project: project.id })}
                    confirmLabel={t('general.save')}
                    onConfirm={save}
                    onCancel={() => setConfirmOpen(false)}
                />
            )}
        </div>
    );
}
